import { useContext } from 'react';
import type { FC } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import type { LocaldataProps } from 'react-localdata';
import { LocaldataContext } from 'react-localdata';
import { CssTransition } from 'sento-transition';

import OddValue from 'app/components/OddValue';
import CompetitorsOddsValues from 'app/components/CompetitorsOddsValues';
import PrivateContent from 'app/common/PrivateContent';
import Icon from 'app/components/Icon';
import ArbitrageIcon from 'app/components/ArbitrageIcon';
import type { MatchPreviewData, MatchPreviewTeam } from 'app/components/MatchPreview/types';
import { COMPETITORS_LOGO } from 'app/config/competitors.config';
import UserContext from 'app/common/UserContext';
import warningIcon from 'app/icons/warning_empty.svg';
import Tooltip from 'app/components/Tooltip';
import { getElapsedDuration, getTimestamp } from 'app/utils/date';

import style from './style.scss';
import { getFilteredOdds, getArbitrage } from './utils';

interface Props {
  match: Pick<MatchPreviewData, 'markets_status' | 'competitors' | 'status' | 'id'>;
  teams: [MatchPreviewTeam, MatchPreviewTeam];
  valueClassName?: string;
  forceDisplayedMarket?: MatchPreviewDisplayedMarket;
  compact?: boolean;
  monospace?: boolean;
  noOverround?: boolean;
  downStatus?: boolean;
}

const oddClassNameMap = {
  active: undefined,
  deactivated: style.marketOdd_deactivated,
  suspended: style.marketOdd_suspended,
  settled: undefined,
  pre_settled: undefined
};

const loaderClassNames = {
  enter: style.loaderEnter,
  enterActive: style.loaderEnterActive,
  exit: style.loaderExit,
  exitActive: style.loaderExitActive
};

const DisplayedMarketOdds: FC<Props> = ({
  match,
  teams,
  valueClassName,
  forceDisplayedMarket = undefined,
  compact = undefined,
  monospace = false,
  noOverround = false,
  downStatus = false
}) => {
  const [t] = useTranslation();
  const {
    localdata: { displayedMarketOnSchedule: displayedMarketFromLocaldata }
  }: LocaldataProps<AppLocaldata> = useContext(LocaldataContext);
  const { preferences } = useContext(UserContext);

  const displayedMarket = forceDisplayedMarket ?? displayedMarketFromLocaldata;

  const matchStatus = match.markets_status?.[displayedMarket];
  const oddStatusClassName = matchStatus ? oddClassNameMap[matchStatus] : undefined;
  const oddClassName = classnames(style.marketOdd, oddStatusClassName);

  const displayCompetitorOdds =
    displayedMarket === 'winner' && // display competitors odds only for winner-2/3-way marker
    preferences.competitors_display &&
    match.competitors.length > 0 &&
    match.competitors.filter(competitor => competitor.markets.length > 0).length > 0 &&
    ['set_up', 'pre_match'].includes(match.status);

  const team1Market = teams[0].markets?.[displayedMarket];
  const team2Market = teams[1].markets?.[displayedMarket];

  const filteredOdds = getFilteredOdds(
    match.competitors,
    'winner-2-way',
    'match',
    preferences.competitors_display ? preferences.competitors_selected : undefined
  );

  return (
    <div className={style.container}>
      <div className={style.pandascoreOdds}>
        <div className={classnames(style.value, valueClassName)}>
          {downStatus ? (
            <Icon className={style.errorIcon} href={warningIcon} />
          ) : (
            <OddValue
              // using key allow not to display evolution arrows when switching displayed market
              key={`${displayedMarket}-odd-value-1`}
              value={team1Market?.proba}
              className={oddClassName}
              increaseClassName={style.oddIndicator_increaseRight}
              decreaseClassName={style.oddIndicator_decreaseRight}
              label={
                team1Market?.indicator && (
                  <span className={style.indicator}>{team1Market.indicator}</span>
                )
              }
              evolutionFrom={teams[0].previousProba}
              monospace={monospace}
              noOverround={noOverround}
            />
          )}
        </div>
        <span className={classnames(style.versus, { [style.compactVersus]: compact })}>
          {team1Market?.line ?? t('events-match_versus')}
        </span>
        <div className={classnames(style.value, valueClassName)}>
          {downStatus ? (
            <Icon className={style.errorIcon} href={warningIcon} />
          ) : (
            <OddValue
              // using key allow not to display evolution arrows when switching displayed market
              key={`${displayedMarket}-odd-value-2`}
              value={team2Market?.proba}
              className={oddClassName}
              increaseClassName={style.oddIndicator_increaseLeft}
              decreaseClassName={style.oddIndicator_decreaseLeft}
              label={
                team2Market?.indicator && (
                  <span className={style.indicator}>{team2Market.indicator}</span>
                )
              }
              evolutionFrom={teams[1].previousProba}
              monospace={monospace}
              labelPlacement="right"
              noOverround={noOverround}
            />
          )}
        </div>
      </div>
      <PrivateContent feature="COMPETITORS">
        {filteredOdds.length > 0 ? (
          <Tooltip
            className={style.competitorsOdds}
            position="bottom"
            content={
              <div className={style.competitorsOddsUpdateTooltip}>
                {filteredOdds.map(competitor => (
                  <div key={competitor.slug} className={style.competitorOddsUpdate}>
                    <Icon
                      className={style.competitorLogoUpdate}
                      href={COMPETITORS_LOGO[competitor.slug]}
                    />
                    <span>{t('events-match_competitors-last_update')}</span>
                    <span className={style.competitorOddsUpdateTime}>
                      {getElapsedDuration(
                        getTimestamp(competitor.market.update_received_at),
                        Date.now(),
                        {
                          days: true,
                          hours: true,
                          minutes: true
                        }
                      )}
                    </span>
                  </div>
                ))}
              </div>
            }
          >
            <CssTransition
              displayed={displayCompetitorOdds}
              classNames={loaderClassNames}
              timeout={150}
            >
              <>
                {filteredOdds.map(competitorOdds => {
                  const isInArbitrage = getArbitrage(competitorOdds);
                  return (
                    <div className={style.competitorsOddsContent} key={competitorOdds.slug}>
                      <CompetitorsOddsValues
                        className={style.competitorOdd}
                        matchId={match.id}
                        team={teams[0]}
                        animationPosition="right"
                        filteredOdds={competitorOdds}
                      />
                      <div className={style.icons_container}>
                        <Icon
                          className={style.competitorLogo}
                          href={COMPETITORS_LOGO[competitorOdds.slug]}
                        />
                        {isInArbitrage && <ArbitrageIcon size="small" />}
                      </div>
                      <CompetitorsOddsValues
                        className={style.competitorOdd}
                        matchId={match.id}
                        team={teams[1]}
                        animationPosition="left"
                        filteredOdds={competitorOdds}
                      />
                    </div>
                  );
                })}
              </>
            </CssTransition>
          </Tooltip>
        ) : null}
      </PrivateContent>
    </div>
  );
};

DisplayedMarketOdds.displayName = 'DisplayedMarketOdds';

export default DisplayedMarketOdds;
